import { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";

import api from "../../services/api";
import Modal from "../../components/ui/Modal";
import { notify } from "../../components/ui/Toast";
import { readApiError } from "../../utils/apiError";
import { relativeTime, exactTime } from "../../utils/time";
import { Confirm, FormError, ModalFoot, PanelBody, isPinfl, PINFL_LENGTH } from "../../components/ui/panelKit";

const LABELS = { pinfl: "PINFL", email: "Email", full_name: "Ism", access: "Daraja" };

const ACCESS = [
  { value: "admin", label: "administrator" },
  { value: "read", label: "oʻqish" },
];

const ACCESS_LABELS = ACCESS.reduce((a, o) => ({ ...a, [o.value]: o.label }), {});

/**
 * Which cabinet logins may administer this client.
 *
 * A grant is made against a PINFL before the person has ever signed in, and
 * nothing secret comes back: they enter through OneID, so there is no password
 * to mint, hand over or intercept. The grant is complete the moment it is
 * saved — which is also why the PINFL has to be right. OneID gives us no email
 * address to fall back on, so a PINFL with a typo produces a grant that will
 * never match anybody, and the person is told they have no access.
 */
const ManagersPanel = ({ clientId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ pinfl: "", full_name: "", email: "", access: "read" });
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [changing, setChanging] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/clients/${clientId}/managers/`);
      setRows(res.data || []);
    } catch (err) {
      setError(readApiError(err, LABELS, "Menejerlar roʻyxatini yuklab boʻlmadi."));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const grant = async () => {
    const pinfl = form.pinfl.trim();
    if (!isPinfl(pinfl)) {
      setFormError(`PINFL ${PINFL_LENGTH} xonali raqam boʻlishi kerak. Hozir ${pinfl.length} belgi kiritilgan.`);
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const res = await api.post(`/clients/${clientId}/managers/`, {
        pinfl,
        access: form.access,
        full_name: form.full_name.trim(),
        email: form.email.trim(),
      });
      notify.success(
        res.data.created_login
          ? "Menejer qoʻshildi va unga yangi login yaratildi. Parol yoʻq — u OneID bilan kiradi."
          : "Menejer qoʻshildi. Mavjud login shu mijoz tizimiga ham bogʻlandi.",
      );
      setOpen(false);
      setForm({ pinfl: "", full_name: "", email: "", access: "read" });
      await load();
    } catch (err) {
      setFormError(readApiError(err, LABELS, "Menejerni qoʻshib boʻlmadi."));
    } finally {
      setSaving(false);
    }
  };

  const changeAccess = async (manager, access) => {
    if (access === manager.access) return;
    setChanging(manager.user_id);
    try {
      const res = await api.patch(`/clients/${clientId}/managers/${manager.user_id}/`, {
        access,
      });
      setRows((list) => list.map((m) => (m.user_id === manager.user_id ? res.data : m)));
      notify.success(`Daraja “${ACCESS_LABELS[access]}” ga oʻzgartirildi`);
    } catch (err) {
      notify.error(readApiError(err, LABELS, "Darajani oʻzgartirib boʻlmadi."));
      // The select is driven by `rows`, so a failed change has to be put back
      // from the server's copy rather than left showing what did not happen.
      await load();
    } finally {
      setChanging("");
    }
  };

  const revoke = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await api.delete(`/clients/${clientId}/managers/${confirm.user_id}/`);
      notify.success("Menejer huquqi olib tashlandi");
      setConfirm(null);
      await load();
    } catch (err) {
      notify.error(readApiError(err, LABELS, "Menejer huquqini olib boʻlmadi."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ms-panel">
      <div className="ms-panel-head">
        <h2>Kabinet menejerlari</h2>
        <div className="ms-toolbar-spacer" />
        <button
          type="button"
          className="ms-btn ms-btn--primary ms-btn--sm"
          onClick={() => {
            setForm({ pinfl: "", full_name: "", email: "", access: "read" });
            setFormError("");
            setOpen(true);
          }}
        >
          <Icon icon="tabler:user-plus" />
          Menejer
        </button>
      </div>

      <PanelBody
        loading={loading}
        error={error}
        empty={!rows.length}
        emptyText="Menejer yoʻq — mijoz kabinetga hech kim kira olmaydi"
      >
        <div className="ms-table-wrap">
          <table className="ms-table ms-table--compact">
            <thead>
              <tr>
                <th style={{ width: 170 }}>PINFL</th>
                <th>Ism</th>
                <th style={{ width: 150 }}>Daraja</th>
                <th style={{ width: 130 }}>Oxirgi kirish</th>
                <th className="ms-td-actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.id}>
                  <td>
                    <span className="ms-mono">{m.pinfl}</span>
                  </td>
                  <td className="ms-td-strong">{m.full_name || m.email || "—"}</td>
                  <td>
                    <select
                      className="ms-select ms-select--sm"
                      value={m.access}
                      disabled={changing === m.user_id}
                      aria-label={`${m.pinfl} uchun daraja`}
                      onChange={(e) => changeAccess(m, e.target.value)}
                    >
                      {ACCESS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td
                    className="ms-td-muted"
                    title={exactTime(m.last_oneid_login_at || m.last_login)}
                  >
                    {m.last_oneid_login_at || m.last_login
                      ? relativeTime(m.last_oneid_login_at || m.last_login)
                      : "hali kirmagan"}
                  </td>
                  <td className="ms-td-actions">
                    <div className="ms-rowacts">
                      <button
                        type="button"
                        className="ms-rowact is-danger"
                        title="Huquqni olib tashlash"
                        aria-label={`${m.pinfl} huquqini olib tashlash`}
                        onClick={() => setConfirm(m)}
                      >
                        <Icon icon="tabler:user-off" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PanelBody>

      <Modal
        open={open}
        title="Kabinet menejeri qoʻshish"
        size="sm"
        onClose={() => setOpen(false)}
        footer={
          <ModalFoot
            onCancel={() => setOpen(false)}
            onSave={grant}
            saving={saving}
            saveLabel="Qoʻshish"
            savingLabel="Qoʻshilmoqda..."
          />
        }
      >
        <div className="ms-form-grid ms-form-grid--stack">
          <div className="ms-field ms-field--full">
            <label htmlFor="ms-mgr-pinfl">
              PINFL <span>*</span>
            </label>
            <input
              id="ms-mgr-pinfl"
              type="text"
              inputMode="numeric"
              maxLength={PINFL_LENGTH}
              placeholder="14 xonali raqam"
              className="ms-mono"
              value={form.pinfl}
              autoFocus
              onChange={(e) =>
                setForm((f) => ({ ...f, pinfl: e.target.value.replace(/\D/g, "") }))
              }
            />
            <span className="ms-field-hint">
              {form.pinfl.length}/{PINFL_LENGTH}. Huquq shu PINFL ga beriladi:
              OneID email qaytarmaydi, shuning uchun kirgan odam faqat PINFL
              boʻyicha topiladi.
            </span>
          </div>

          <div className="ms-field ms-field--full">
            <label htmlFor="ms-mgr-access">
              Daraja <span>*</span>
            </label>
            <select id="ms-mgr-access" value={form.access} onChange={set("access")}>
              {ACCESS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <span className="ms-field-hint">
              “oʻqish” — faqat koʻradi; “administrator” — aʼzolar va manbalarni
              ham oʻzgartiradi.
            </span>
          </div>

          <div className="ms-field ms-field--full">
            <label htmlFor="ms-mgr-name">Ism</label>
            <input
              id="ms-mgr-name"
              type="text"
              maxLength={200}
              placeholder="Ixtiyoriy"
              value={form.full_name}
              onChange={set("full_name")}
            />
          </div>

          <div className="ms-field ms-field--full">
            <label htmlFor="ms-mgr-email">Email</label>
            <input
              id="ms-mgr-email"
              type="email"
              placeholder="Ixtiyoriy"
              value={form.email}
              onChange={set("email")}
            />
            <span className="ms-field-hint">
              Email faqat roʻyxatda odamni tanib olish uchun — u bilan kirilmaydi.
            </span>
          </div>

          <FormError>{formError}</FormError>
        </div>
      </Modal>

      <Confirm
        open={!!confirm}
        busy={busy}
        name={confirm ? `${confirm.pinfl}${confirm.full_name ? ` — ${confirm.full_name}` : ""}` : ""}
        title="Menejer huquqini olib tashlash"
        message="Bu odam shu mijoz tizimini boshqara olmaydi. Uning logini oʻchmaydi — boshqa mijoz tizimlaridagi huquqlari saqlanadi."
        confirmLabel="Olib tashlash"
        cancelLabel="Bekor qilish"
        busyLabel="Olib tashlanmoqda..."
        onConfirm={revoke}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default ManagersPanel;
